import React, { useEffect, useMemo, useState } from 'react';
/**
 * TableReadingRenderer — Select a row, cell, or entire column from a data table.
 *
 * Supports three selection modes via `data.selectType`:
 *   - "row"    (default) → user clicks an entire row. Answer = row index.
 *   - "cell"            → user clicks a single cell. Answer = flat cell index
 *                          (row × numCols + col), matching the convention in
 *                          STU-TABEL seeds (e.g. STU-TABEL-003 correctSelection 11
 *                          on a 3×4 grid = row 2, col 3).
 *   - "column"          → user clicks any cell in a column to select the whole
 *                          column. Answer = column index.
 *
 * Validation: `validateTableReading` compares `Number(userAnswer)` to
 * `config.correctSelection`. The renderer emits the index appropriate for the
 * active mode, so no validator change is needed.
 *
 * Selected-state styling follows audit B18 (§8.6) — bg-indigo-100, indigo text,
 * 4px left-border for row mode, ring for cell/column mode.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
type SelectType = 'row' | 'cell' | 'column';
export function TableReadingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  // Headers + rows can live either at the root of `data` (STU-TABEL convention)
  // or nested under `data.table` (older convention). Support both.
  const headers: string[] = config.headers || config.table?.headers || [];
  const rows: string[][] = config.rows || config.table?.rows || [];
  const numCols = headers.length || rows[0]?.length || 0;
  const selectType: SelectType = config.selectType === 'cell' || config.selectType === 'column' ? config.selectType : 'row';
  // Selection state — a single number that means:
  //   row   → selected row index
  //   cell  → flat cell index (row * numCols + col)
  //   column→ selected column index
  const [selected, setSelected] = useState<number | null>(null);
  useEffect(() => {
    setSelected(null);
  }, [question.id]);
  useEffect(() => {
    onReadyChange?.(selected !== null);
  }, [selected, onReadyChange]);
  useEdgeTriggeredSubmit(selected !== null ? String(selected) : '', shouldValidate, onAnswer, question.id);
  const cellIndex = (rowIdx: number, colIdx: number) => rowIdx * Math.max(numCols, 1) + colIdx;
  // ── Keyboard nav helpers ────────────────────────────────────────────────
  const focusElement = (id: string) => {
    document.getElementById(id)?.focus();
  };
  const handleRowKeyDown = (e: React.KeyboardEvent, rowIndex: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelected(rowIndex);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusElement(`tr-row-${question.id}-${(rowIndex + 1) % rows.length}`);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusElement(`tr-row-${question.id}-${(rowIndex - 1 + rows.length) % rows.length}`);
    }
  };
  const handleCellKeyDown = (e: React.KeyboardEvent, rowIdx: number, colIdx: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelected(cellIndex(rowIdx, colIdx));
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (rowIdx + 1) % rows.length;
      focusElement(`tr-cell-${question.id}-${next}-${colIdx}`);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = (rowIdx - 1 + rows.length) % rows.length;
      focusElement(`tr-cell-${question.id}-${next}-${colIdx}`);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (colIdx + 1) % numCols;
      focusElement(`tr-cell-${question.id}-${rowIdx}-${next}`);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = (colIdx - 1 + numCols) % numCols;
      focusElement(`tr-cell-${question.id}-${rowIdx}-${next}`);
    }
  };
  const handleColumnKeyDown = (e: React.KeyboardEvent, colIdx: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelected(colIdx);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusElement(`tr-col-${question.id}-${(colIdx + 1) % numCols}`);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusElement(`tr-col-${question.id}-${(colIdx - 1 + numCols) % numCols}`);
    }
  };
  // ── Derived selection helpers per mode ──────────────────────────────────
  const isRowSelected = (rowIdx: number) => selectType === 'row' && selected === rowIdx;
  const isCellSelected = (rowIdx: number, colIdx: number) => selectType === 'cell' && selected === cellIndex(rowIdx, colIdx);
  const isColumnSelected = (colIdx: number) => selectType === 'column' && selected === colIdx;
  // ── Footer hint per mode ────────────────────────────────────────────────
  const footerHint = useMemo(() => {
    if (selectType === 'cell') return 'Klik op de cel met het juiste antwoord (of gebruik de pijltjestoetsen).';
    if (selectType === 'column') return 'Klik op een kolom (of gebruik ← →).';
    return 'Klik op een rij of gebruik de pijltjestoetsen.';
  }, [selectType]);
  // ── Empty/invalid data guard ────────────────────────────────────────────
  if (rows.length === 0 || numCols === 0) {
    return <div className="text-center p-8 bg-red-50 rounded-xl border-2 border-red-200" data-id="element-10891">
        <p className="text-red-700 font-bold" data-id="element-10892">⚠️ Geen tabelgegevens gevonden</p>
      </div>;
  }
  return <div className="w-full max-w-2xl mx-auto overflow-hidden rounded-xl border border-slate-200 shadow-sm" data-id="element-10893">
      <table className="w-full text-left border-collapse" role="grid" aria-label="Tabel met gegevens" data-id="element-10894">
        <thead data-id="element-10895">
          <tr className="bg-slate-50 border-b border-slate-200" role="row" data-id="element-10896">
            {headers.map((header: string, colIdx: number) => {
            const colSel = isColumnSelected(colIdx);
            const clickableHeader = selectType === 'column';
            return <th key={colIdx} id={clickableHeader ? `tr-col-${question.id}-${colIdx}` : undefined} role={clickableHeader ? 'columnheader' : 'columnheader'} aria-selected={clickableHeader ? colSel : undefined} tabIndex={clickableHeader ? 0 : -1} onClick={clickableHeader ? () => setSelected(colIdx) : undefined} onKeyDown={clickableHeader ? e => handleColumnKeyDown(e, colIdx) : undefined} className={`p-4 font-bold transition-colors ${clickableHeader ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500' : ''} ${colSel ? 'bg-indigo-100 text-indigo-900 ring-2 ring-inset ring-indigo-500' : 'text-slate-700'}`} data-id="element-10897">
                  <LatexRenderer content={header} data-id="element-10898" />
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-10899">
          {rows.map((row: string[], rowIndex: number) => {
          const rowSel = isRowSelected(rowIndex);
          // ── Row-mode: whole <tr> is the interactive unit ───────────
          if (selectType === 'row') {
            return <tr key={rowIndex} id={`tr-row-${question.id}-${rowIndex}`} role="row" aria-selected={rowSel} tabIndex={0} onClick={() => setSelected(rowIndex)} onKeyDown={e => handleRowKeyDown(e, rowIndex)} className={`cursor-pointer transition-colors border-b border-slate-100 last:border-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 ${rowSel ? 'bg-indigo-100 text-indigo-900 font-semibold border-l-4 border-l-indigo-500' : 'hover:bg-slate-50 text-slate-600'}`} data-id="element-10900">
                  {row.map((cell: string, cellIdx: number) => <td key={cellIdx} className="p-4 min-h-[56px]" role="gridcell" data-id="element-10901">
                      <LatexRenderer content={cell} data-id="element-10902" />
                    </td>)}
                </tr>;
          }
          // ── Cell- or column-mode: each <td> is interactive ─────────
          return <tr key={rowIndex} role="row" className="border-b border-slate-100 last:border-0" data-id="element-10903">
                {row.map((cell: string, colIdx: number) => {
              const cellSel = isCellSelected(rowIndex, colIdx);
              const colSel = isColumnSelected(colIdx);
              const isInteractive = selectType === 'cell' || selectType === 'column';
              const onClick = () => {
                if (selectType === 'cell') {
                  setSelected(cellIndex(rowIndex, colIdx));
                } else if (selectType === 'column') {
                  setSelected(colIdx);
                }
              };
              const tdId = selectType === 'cell' ? `tr-cell-${question.id}-${rowIndex}-${colIdx}` : undefined;
              const baseTone = colSel ? 'bg-indigo-100 text-indigo-900 font-semibold' : cellSel ? 'bg-indigo-100 text-indigo-900 font-semibold ring-2 ring-inset ring-indigo-500' : 'text-slate-700 hover:bg-slate-50';
              return <td key={colIdx} id={tdId} role="gridcell" aria-selected={cellSel || colSel} tabIndex={isInteractive ? 0 : -1} onClick={isInteractive ? onClick : undefined} onKeyDown={selectType === 'cell' ? e => handleCellKeyDown(e, rowIndex, colIdx) : selectType === 'column' ? e => handleColumnKeyDown(e, colIdx) : undefined} className={`p-4 min-h-[56px] transition-colors ${isInteractive ? 'cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500' : ''} ${baseTone}`} data-id="element-10904">
                      <LatexRenderer content={cell} data-id="element-10905" />
                    </td>;
            })}
              </tr>;
        })}
        </tbody>
      </table>
      <div className="bg-slate-50 p-3 text-center text-sm text-slate-500 border-t border-slate-200" data-id="element-10906">
        {footerHint}
      </div>
    </div>;
}